import { useState, useEffect } from "react";
import {
    Box, Grid, Card, CardContent, Typography, Chip, Avatar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Alert, Divider, LinearProgress, Stack,
    Badge, IconButton, Tooltip,
    AlertTitle,Button
} from "@mui/material";
import {
    ShoppingCart, AttachMoney, Inventory, TrendingUp,
    HourglassEmpty, CheckCircle, Cancel, Star, Home,
    Campaign, Warning, ErrorOutline, Refresh
} from "@mui/icons-material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import apiClient from "../utils/apiClient";
import { useNavigate } from "react-router-dom";
import { pageRoute } from "../utils/common";
import DownloadIcon from "@mui/icons-material/Download";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
    palette: {
        mode: "dark",
        primary: { main: "#6EE7B7" },
        secondary: { main: "#F472B6" },
        background: { default: "#0F172A", paper: "#1E293B" },
        success: { main: "#34D399" },
        warning: { main: "#FBBF24" },
        error: { main: "#F87171" },
        info: { main: "#60A5FA" },
    },
    typography: {
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        h4: { fontWeight: 800, letterSpacing: "-0.5px" },
        h6: { fontWeight: 700 },
        subtitle2: { fontWeight: 600 },
    },
    shape: { borderRadius: 16 },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    border: "1px solid rgba(255,255,255,0.06)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: { backgroundColor: "rgba(110,231,183,0.08)", fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "#6EE7B7" },
            },
        },
    },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const statusColor = (s) =>
    ({ delivered: "success", pending: "warning", cancelled: "error", processing: "info" }[s] ?? "default");

const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso)) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color, sub }) {
    return (
        <Card sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", bgcolor: alpha(color, 0.12) }} />
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                    <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: alpha(color, 0.15), display: "flex" }}>
                        <Icon sx={{ color, fontSize: 22 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
                </Stack>
                <Typography variant="h4" sx={{ color, mb: 0.5 }}>{value}</Typography>
                {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
            </CardContent>
        </Card>
    );
}

function ProductBadges({ counts }) {
    const badges = [
        { label: "Pending", val: counts.pendingApproval, icon: <HourglassEmpty sx={{ fontSize: 14 }} />, color: "warning" },
        { label: "Approved", val: counts.approvedProducts, icon: <CheckCircle sx={{ fontSize: 14 }} />, color: "success" },
        { label: "Rejected", val: counts.rejectedProducts, icon: <Cancel sx={{ fontSize: 14 }} />, color: "error" },
        { label: "Banner", val: counts.bannerProducts, icon: <Campaign sx={{ fontSize: 14 }} />, color: "info" },
        { label: "Featured", val: counts.featuredProducts, icon: <Star sx={{ fontSize: 14 }} />, color: "secondary" },
        // { label: "Homepage", val: counts.homepageProducts, icon: <Home sx={{ fontSize: 14 }} />, color: "primary" },
    ];
    return (
        <Card sx={{ height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" mb={2.5}>Product Status</Typography>
                <Grid container spacing={1.5}>
                    {badges.map(({ label, val, icon, color }) => (
                        <Grid item size={{ xs: 6 }} key={label}>
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette[color]?.main || "#fff", 0.08), border: `1px solid ${alpha(theme.palette[color]?.main || "#fff", 0.15)}` }}>
                                <Stack direction="row" alignItems="center" spacing={0.7} mb={0.5}>
                                    <Box sx={{ color: `${color}.main` }}>{icon}</Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
                                </Stack>
                                <Typography variant="h6" color={`${color}.main`}>{val}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
}

function StockSection({ lowStock, outOfStock }) {
    const navigate = useNavigate()

    const pageChange = (url) => {
        navigate(url)
    }
    return (
        <Card>
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
                    <Warning sx={{ color: "warning.main", fontSize: 20 }} />
                    <Typography variant="h6">Stock Alerts</Typography>
                    {outOfStock.length > 0 && (
                        <Chip label={`${outOfStock.length} Out of Stock`} color="error" size="small" />
                    )}
                </Stack>
                {lowStock.length === 0 && outOfStock.length === 0 ? (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>All products are well stocked!</Alert>
                ) : (
                    <Stack spacing={1.5}>
                        {outOfStock.map((p) => (
                            <Stack key={p.id} direction="row" alignItems="center" spacing={2} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha("#F87171", 0.08), border: "1px solid rgba(248,113,113,0.2)" }}>
                                <ErrorOutline sx={{ color: "error.main", fontSize: 18 }} />
                                <Typography variant="body2" fontWeight={600} flex={1} onClick={() => pageChange(pageRoute.productView + p.id)}
                                >{p.name}</Typography>
                                <Chip label="Out of Stock" color="error" size="small" />
                            </Stack>
                        ))}
                        {lowStock.map((p) => (
                            <Box key={p.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha("#FBBF24", 0.06), border: "1px solid rgba(251,191,36,0.15)" }}>
                                <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                                    <Warning sx={{ color: "warning.main", fontSize: 18 }} />
                                    <Typography variant="body2" fontWeight={600} flex={1} onClick={() => pageChange(pageRoute.productView + p.id)}
                                    >{p.name}</Typography>
                                    <Chip label={`${p.totalStock} left`} color="warning" size="small" />
                                </Stack>
                                <LinearProgress variant="determinate" value={Math.min((Number(p.totalStock) / 10) * 100, 100)} color="warning" sx={{ borderRadius: 4, height: 4 }} />
                            </Box>
                        ))}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}

function RecentOrdersTable({ orders }) {
    return (
        <Card>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" mb={2.5}>Recent Orders</Typography>
                <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "transparent" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Order</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="right">Time</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((o) => (
                                <TableRow key={o.id} sx={{ "&:hover": { bgcolor: "rgba(255,255,255,0.03)" }, transition: "background 0.15s" }}>
                                    <TableCell>
                                        <Typography variant="caption" color="primary.main" fontWeight={700}>{o.orderNumber}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar sx={{ width: 26, height: 26, fontSize: 12, bgcolor: "secondary.main" }}>
                                                {o.customerName[0].toUpperCase()}
                                            </Avatar>
                                            <Typography variant="body2">{o.customerName}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={o.order_status} color={statusColor(o.order_status)} size="small" sx={{ textTransform: "capitalize", fontWeight: 700, fontSize: "0.7rem" }} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={700} color="primary.main">{fmt(o.vendor_total)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="caption" color="text.secondary">{timeAgo(o.createdAt)}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
}

function TopSelling({ products }) {
    const navigate = useNavigate()

    const pageChange = (url) => {
        navigate(url)
    }
    const max = Math.max(...products.map((p) => Number(p.totalSales)));
    return (
        <Card>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" mb={2.5}>Top Selling Products</Typography>
                <Stack spacing={2}>
                    {products.map((p, i) => {
                        if (Number(p.totalSales) > 0) {
                            return (
                                <Box key={p.id}>
                                    <Stack direction="row" alignItems="center" spacing={1.5} mb={0.8}>
                                        <Typography variant="caption" sx={{ width: 18, color: i === 0 ? "warning.main" : "text.secondary", fontWeight: 800 }}>#{i + 1}</Typography>
                                        <Typography variant="body2" fontWeight={600} flex={1} onClick={() => pageChange(pageRoute.productView + p.id)}
                                        >{p.name}</Typography>
                                        <Typography variant="caption" color="primary.main" fontWeight={700}>{p.totalSales} sold</Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(Number(p.totalSales) / max) * 100}
                                        sx={{ borderRadius: 4, height: 5, bgcolor: "rgba(255,255,255,0.06)", "& .MuiLinearProgress-bar": { background: i === 0 ? "linear-gradient(90deg,#6EE7B7,#3B82F6)" : i === 1 ? "linear-gradient(90deg,#F472B6,#A78BFA)" : "linear-gradient(90deg,#FBBF24,#F97316)" } }}
                                    />
                                </Box>
                            )
                        } else {
                            return null
                        }
                    }
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

function RecentProducts({ products }) {
    const navigate = useNavigate()

    const pageChange = (url) => {
        navigate(url)
    }
    return (
        <Card>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" mb={2.5}>Recently Added</Typography>
                <Stack spacing={1.5}>
                    {products.map((p) => (
                        <Stack key={p.id} direction="row" alignItems="center" spacing={2} sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: "rgba(110,231,183,0.1)", fontSize: 12, fontWeight: 700, color: "primary.main" }}>
                                {p.name[0].toUpperCase()}
                            </Avatar>
                            <Box flex={1} minWidth={0}>
                                {/* <Typography variant="body2" fontWeight={600} noWrap                       onClick={() => pageChange(pageRoute.productView+p.id)}
                                > */}
                                <Typography variant="body2" fontWeight={600} flex={1} onClick={() => pageChange(pageRoute.productView + p.id)}
                                >

                                    {p.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{new Date(p.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={700} color="primary.main">{fmt(p.price)}</Typography>
                        </Stack>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function VendorDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // const navigate = useNavigate()

    // const pageChange = (url) => {
    //     navigate(url)
    // }

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            // ── DEMO FALLBACK: remove the block below when your API is ready ──
            await new Promise((r) => setTimeout(r, 900));

            const res = await apiClient.get("/api/vendor/dashboard");
            setData(res.data?.data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

  const handleDownload = () => {
    
    const link = document.createElement("a");
    link.href = "/vendor-policy-framework.pdf";
    link.download = "Vendor-Policy-Framework.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
    return (
        <Box sx={{ minHeight: "100vh" }}>
            {/* Header */}
              <Alert
                    severity="info"
                    sx={{
                      marginBottom:'10px',
                      borderRadius: 3,
                      alignItems: "center",
                    }}
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                      >
                        Download
                      </Button>
                    }
                  >
                    <AlertTitle>Vendor Policy Framework</AlertTitle>
            
                    <Typography variant="body2">
                      Please download and review the Vendor Policy Framework
                      document before continuing registration or onboarding.
                      Make sure you understand all terms, conditions, and
                      compliance requirements.
                    </Typography>
                  </Alert>

            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
                <Box>
                    <Typography variant="h4" color="primary.main">Vendor Dashboard</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={load} disabled={loading} sx={{ color: "primary.main", bgcolor: alpha("#6EE7B7", 0.08), "&:hover": { bgcolor: alpha("#6EE7B7", 0.15) } }}>
                        <Refresh sx={{ animation: loading ? "spin 1s linear infinite" : "none", "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } } }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Loading */}
            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                    <Stack alignItems="center" spacing={2}>
                        <CircularProgress color="primary" size={48} thickness={3} />
                        <Typography color="text.secondary">Loading dashboard...</Typography>
                    </Stack>
                </Box>
            )}

            {/* Error */}
            {error && !loading && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }} action={
                    <IconButton color="inherit" size="small" onClick={load}><Refresh fontSize="inherit" /></IconButton>
                }>
                    {error}
                </Alert>
            )}

            {/* Dashboard */}
            {data && !loading && (
                <Stack spacing={3}>
                    {/* Stat Cards Row */}
                    <Grid container spacing={2.5}>
                        {[
                            {
                                icon: ShoppingCart, label: "Total Orders", value: data.counts.totalOrders, color: "#60A5FA",
                                // sub: "All time orders"
                            },
                            // { icon: AttachMoney, label: "Total Revenue", value: fmt(data.counts.totalRevenue), color: "#6EE7B7", sub: "Lifetime earnings" },
                            {
                                icon: Inventory, label: "Total Products", value: data.counts.totalProducts, color: "#F472B6",
                                // sub: `${data.counts.activeProducts} active`
                            },
                            {
                                icon: TrendingUp, label: "Active Products", value: data.counts.activeProducts, color: "#FBBF24",
                                // sub: `${data.counts.inactiveProducts} inactive` 
                            },
                            { icon: TrendingUp, label: "Inative Products", value: data.counts.inactiveProducts, color: "#fb245a", },
                        ].map((s) => (
                            <Grid item size={{ xs: 12, sm: 6, lg: 3 }} key={s.label}>
                                <StatCard {...s} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Product Badges + Stock */}
                    <Grid container spacing={2.5}>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <ProductBadges counts={data.counts} />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <StockSection lowStock={data.stock.lowStockProducts} outOfStock={data.stock.outOfStockProducts} />
                        </Grid>
                    </Grid>

                    {/* Orders Table */}
                    <RecentOrdersTable orders={data.recentOrders} />

                    {/* Top Selling + Recent Products */}
                    {/* <Grid container spacing={2.5}>
                        <Grid item size={{ xs: 12, md: 7 }}>
                            <TopSelling products={data.topSellingProducts} />
                        </Grid>
                        <Grid item size={{ xs: 12, md: 5 }}>
                            <RecentProducts products={data.recentProducts} />
                        </Grid>
                    </Grid> */}
                </Stack>
            )}
        </Box>
    );
}